/**
 * TripDetailsModal — full booking detail view for the customer portal.
 *
 * Surfaces every field the customer should see (route, schedule, fare
 * breakdown, driver, payment, cancellation context) and offers action
 * buttons that adapt to the booking's current status.
 *
 * Reschedule is intentionally routed through WhatsApp (wa.me deep link
 * with a pre-filled message). For a 1-driver pilot, founder negotiates
 * a new slot directly — no backend self-serve reschedule flow yet.
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  MapPin, Calendar, Users, Car, Phone, IndianRupee, MessageCircle, CreditCard,
  Ban, AlertCircle, Headphones, Download, Loader2,
} from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Customer JWT is stored in sessionStorage by CustomerPortal (not as an
// axios default header), so per-request calls must attach it explicitly.
const CUSTOMER_ACCESS_KEY = "sarathix_customer_access";
function customerAuthHeader() {
  const t = sessionStorage.getItem(CUSTOMER_ACCESS_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const STATUS_COLORS = {
  PENDING: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  PENDING_REVIEW: "bg-red-500/15 text-red-400 border-red-500/30",
  CONFIRMED: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  IN_PROGRESS: "bg-green-500/15 text-green-400 border-green-500/30",
  COMPLETED: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  DECLINED: "bg-red-900/15 text-red-300 border-red-900/30",
  CANCELLED: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

const CANCELLABLE_STATUSES = ["PENDING", "PENDING_REVIEW", "CONFIRMED"];

// Production WhatsApp number (founder ops line). E.164 without leading "+".
const SUPPORT_WA = "919187057303";

const fmtCurrency = (n) => {
  if (n == null) return null;
  if (typeof n === "number") return `₹${n.toLocaleString("en-IN")}`;
  return n;
};

const fmtTs = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch {
    return iso;
  }
};

// Small primitive — labelled detail row used throughout the modal.
const DetailRow = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-start gap-3" data-testid={testId}>
    <Icon className="w-4 h-4 text-sarathi-primary mt-0.5 shrink-0" />
    <div className="min-w-0 flex-1">
      <p className="text-sarathi-text-muted text-[11px] uppercase tracking-wider">{label}</p>
      <p className="text-white text-sm break-words">{value || "—"}</p>
    </div>
  </div>
);

export default function TripDetailsModal({
  booking, open, onClose, onCancelClick,
}) {
  // Hooks must run on every render — keep above any early return.
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  // Optimistic override for payment status — set when we reconcile with
  // Razorpay live and discover the customer has actually paid. Lets the
  // Pay Now button disappear immediately without waiting for the parent
  // to re-fetch the booking list.
  const [reconciledPaid, setReconciledPaid] = useState(false);

  // When the modal opens on a COMPLETED + has-pay-link + locally-unpaid
  // booking, ask the backend to reconcile against Razorpay's live status.
  // Catches the common case where the webhook didn't fire (mis-configured
  // URL, network drop) and our DB shows "unpaid" even though the customer
  // already paid via the Razorpay link. Idempotent + cheap (one fetch).
  useEffect(() => {
    if (!open || !booking) return;
    const status = (booking.status || "").toUpperCase();
    const ps = (booking.payment_status || "").toLowerCase();
    const alreadyPaid = ps === "paid" || ps === "completed";
    if (status !== "COMPLETED" || !booking.payment_link_url || alreadyPaid) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(
          `${API}/customer/bookings/${booking.booking_reference}/payment-status`,
          {},
          { headers: customerAuthHeader() },
        );
        if (!cancelled && res?.data?.paid) {
          setReconciledPaid(true);
        }
      } catch {
        // Swallow — reconcile is a best-effort optimisation. The user can
        // still tap Pay Now if our local state is genuinely the truth.
      }
    })();
    return () => { cancelled = true; };
  }, [open, booking]);

  if (!booking) return null;
  const status = (booking.status || "PENDING").toUpperCase();
  const fare = booking.fare_data || {};
  const total = fare.totalFare ?? fare.total ?? fare.displayTotal;
  const paymentStatus = (booking.payment_status || "").toLowerCase();
  const isPaid = reconciledPaid || paymentStatus === "paid" || paymentStatus === "completed";
  const hasPayLink = Boolean(booking.payment_link_url);
  const showCancel = CANCELLABLE_STATUSES.includes(status);
  const showReschedule = CANCELLABLE_STATUSES.includes(status);
  const showPayNow = status === "COMPLETED" && !isPaid && hasPayLink;
  const showDriverContact = status === "IN_PROGRESS" && booking.driver_phone;
  // Invoice download only after the ride is delivered AND payment is in.
  // Matches the backend's `build_invoice_pdf` eligibility gate so customers
  // never see a button that returns 409.
  const showInvoiceDownload = (status === "COMPLETED" || status === "PAID") && isPaid;

  // Download tax-invoice PDF. We use the customer JWT (already attached as
  // a default axios header by AuthContext) and let the browser save the
  // file via a blob URL — no full-page navigation, no popup blocker risk.
  const handleDownloadInvoice = async () => {
    setDownloadingInvoice(true);
    try {
      const res = await axios.get(
        `${API}/customer/bookings/${booking.booking_reference}/invoice.pdf`,
        { responseType: "blob", headers: customerAuthHeader() },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Backend sets the actual filename via Content-Disposition, but we
      // also supply one as a fallback for browsers that ignore the header
      // on blob downloads.
      a.download = `Sarathi-X_Invoice_${booking.booking_reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice downloaded");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Blob error responses need to be re-read as text to get the message
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          toast.error(parsed.detail || "Failed to download invoice");
        } catch {
          toast.error("Failed to download invoice");
        }
      } else {
        toast.error(detail || "Failed to download invoice");
      }
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const rescheduleHref =
    `https://wa.me/${SUPPORT_WA}?text=${encodeURIComponent(
      `Hi Sarathi-X, I'd like to reschedule booking ${booking.booking_reference}. ` +
      `Current slot: ${booking.date} at ${booking.time}. New preferred time: `
    )}`;

  const supportHref =
    `https://wa.me/${SUPPORT_WA}?text=${encodeURIComponent(
      `Hi Sarathi-X, I have a question about booking ${booking.booking_reference}.`
    )}`;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="trip-details-modal"
        className="bg-sarathi-surface border-white/10 max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 pr-6">
            <DialogTitle className="text-white font-mono text-base" data-testid="trip-modal-ref">
              {booking.booking_reference}
            </DialogTitle>
            <span
              className={`text-[10px] px-2 py-0.5 rounded-full border whitespace-nowrap ${
                STATUS_COLORS[status] || STATUS_COLORS.PENDING
              }`}
              data-testid="trip-modal-status"
            >
              {status}
            </span>
          </div>
          <DialogDescription className="text-sarathi-text-secondary text-xs">
            {booking.ride_type === "airport" ? "Airport transfer" : "Outstation"} · Booked {fmtTs(booking.created_at) || "recently"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Route */}
          <div className="space-y-3">
            <DetailRow icon={MapPin} label="Pickup" value={booking.pickup_location} testId="trip-modal-pickup" />
            <DetailRow icon={MapPin} label="Drop" value={booking.drop_location} testId="trip-modal-drop" />
          </div>

          <div className="h-px bg-white/5" />

          {/* Schedule */}
          <div className="grid grid-cols-2 gap-3">
            <DetailRow icon={Calendar} label="Date & time" value={`${booking.date || "—"} · ${booking.time || "—"}`} testId="trip-modal-datetime" />
            <DetailRow icon={Users} label="Passengers" value={booking.passengers || 1} testId="trip-modal-passengers" />
          </div>

          {/* Fare breakdown */}
          {total != null && (
            <>
              <div className="h-px bg-white/5" />
              <div className="space-y-2">
                <p className="text-sarathi-text-muted text-[11px] uppercase tracking-wider">Fare</p>
                {fare.baseFare != null && (
                  <div className="flex justify-between text-xs">
                    <span className="text-sarathi-text-secondary">Base</span>
                    <span className="text-white">{fmtCurrency(fare.baseFare)}</span>
                  </div>
                )}
                {fare.tolls != null && fare.tolls > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-sarathi-text-secondary">Tolls</span>
                    <span className="text-white">{fmtCurrency(fare.tolls)}</span>
                  </div>
                )}
                {fare.gst != null && fare.gst > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-sarathi-text-secondary">GST</span>
                    <span className="text-white">{fmtCurrency(fare.gst)}</span>
                  </div>
                )}
                {fare.nightSurcharge != null && fare.nightSurcharge > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-sarathi-text-secondary">Night surcharge</span>
                    <span className="text-white">{fmtCurrency(fare.nightSurcharge)}</span>
                  </div>
                )}
                <div className="h-px bg-white/5 my-1" />
                <div className="flex justify-between items-baseline">
                  <span className="text-white text-sm font-semibold">Total</span>
                  <span className="text-sarathi-primary font-outfit font-bold text-lg" data-testid="trip-modal-total">
                    {fmtCurrency(total)}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* Driver + vehicle (once assigned) */}
          {(booking.driver_name || booking.vehicle_registration) && (
            <>
              <div className="h-px bg-white/5" />
              <div className="space-y-3">
                <p className="text-sarathi-text-muted text-[11px] uppercase tracking-wider">Driver & vehicle</p>
                {booking.driver_name && (
                  <DetailRow icon={Phone} label="Driver" value={`${booking.driver_name}${booking.driver_phone ? ` · ${booking.driver_phone}` : ""}`} testId="trip-modal-driver" />
                )}
                {(booking.vehicle_name || booking.vehicle_registration) && (
                  <DetailRow
                    icon={Car} label="Vehicle"
                    value={`${booking.vehicle_name || "Vehicle"}${booking.vehicle_registration ? ` · ${booking.vehicle_registration}` : ""}`}
                    testId="trip-modal-vehicle"
                  />
                )}
              </div>
            </>
          )}

          {/* Payment status (only for COMPLETED) */}
          {status === "COMPLETED" && (
            <>
              <div className="h-px bg-white/5" />
              <DetailRow
                icon={IndianRupee} label="Payment"
                value={isPaid ? "Paid · thank you!" : hasPayLink ? "Payment link sent" : "Awaiting payment instructions"}
                testId="trip-modal-payment"
              />
            </>
          )}

          {/* Cancellation context */}
          {status === "CANCELLED" && (
            <>
              <div className="h-px bg-white/5" />
              <div className="bg-slate-500/10 border border-slate-500/30 rounded-lg p-3 space-y-2" data-testid="trip-modal-cancellation">
                <div className="flex items-center gap-2">
                  <Ban className="w-4 h-4 text-slate-300" />
                  <p className="text-slate-200 text-sm font-semibold">
                    Cancelled {booking.cancelled_by ? `by ${booking.cancelled_by}` : ""}
                  </p>
                </div>
                {booking.cancellation_reason && (
                  <p className="text-sarathi-text-secondary text-xs">
                    <span className="text-sarathi-text-muted">Reason:</span> {booking.cancellation_reason}
                  </p>
                )}
                {booking.cancelled_at && (
                  <p className="text-sarathi-text-muted text-xs">on {fmtTs(booking.cancelled_at)}</p>
                )}
              </div>
            </>
          )}

          {/* Declined message */}
          {status === "DECLINED" && (
            <div className="bg-red-900/10 border border-red-900/30 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-300 shrink-0 mt-0.5" />
              <p className="text-red-200 text-xs leading-relaxed">
                We couldn&apos;t accommodate this booking. Our team has reached out on WhatsApp with the next available slot.
              </p>
            </div>
          )}
        </div>

        {/* Action buttons — status-dependent */}
        <div className="pt-4 mt-2 border-t border-white/5 space-y-2" data-testid="trip-modal-actions">
          {showPayNow && (
            <a
              href={booking.payment_link_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
              data-testid="trip-modal-pay-now-btn"
            >
              <Button className="w-full bg-sarathi-primary hover:bg-sarathi-primary-hover text-white h-11">
                <CreditCard className="w-4 h-4 mr-2" />
                Pay now
              </Button>
            </a>
          )}

          {showDriverContact && (
            <a
              href={`tel:${booking.driver_phone}`}
              className="block"
              data-testid="trip-modal-call-driver-btn"
            >
              <Button className="w-full bg-sarathi-primary hover:bg-sarathi-primary-hover text-white h-11">
                <Phone className="w-4 h-4 mr-2" />
                Call driver
              </Button>
            </a>
          )}

          {showInvoiceDownload && (
            <Button
              variant="outline"
              className="w-full border-white/10 hover:border-sarathi-primary/40 text-white h-11"
              onClick={handleDownloadInvoice}
              disabled={downloadingInvoice}
              data-testid="trip-modal-download-invoice-btn"
            >
              {downloadingInvoice ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {downloadingInvoice ? "Generating invoice..." : "Download tax invoice"}
            </Button>
          )}

          {showReschedule && (
            <a
              href={rescheduleHref}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
              data-testid="trip-modal-reschedule-btn"
            >
              <Button
                variant="outline"
                className="w-full border-white/10 hover:border-sarathi-primary/40 text-white h-11"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Reschedule via WhatsApp
              </Button>
            </a>
          )}

          {showCancel && (
            <Button
              onClick={() => { onCancelClick(booking); onClose(); }}
              variant="outline"
              className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 h-11"
              data-testid="trip-modal-cancel-btn"
            >
              <Ban className="w-4 h-4 mr-2" />
              Cancel ride
            </Button>
          )}

          {/* Always show a support link as a final escape hatch */}
          {!showReschedule && !showCancel && !showPayNow && !showDriverContact && (
            <a
              href={supportHref}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
              data-testid="trip-modal-support-btn"
            >
              <Button
                variant="outline"
                className="w-full border-white/10 hover:border-sarathi-primary/40 text-white h-11"
              >
                <Headphones className="w-4 h-4 mr-2" />
                Contact support
              </Button>
            </a>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
